import React, { useEffect, useRef, useState } from 'react'
import CouponBox from '../../CouponBox'
import CouponConfigurableLabel from '../../CouponConfigurableLabel'
import MarketDetailsSelector from './MarketDetailsSelector';
import { Button, Checkbox } from '@mui/material';
import { AddCircleOutlineOutlined } from '@material-ui/icons';
import DoneIcon from '@mui/icons-material/Done';
import { addSchemeLocationBifurcationsList, addSchemeOutletBifurcationsList, deleteAllMarketFilters, deleteAllOutletFilters, deleteMarketFilter, deleteOutletFilter, resetExclusionData, resetInclusionData, updateSectionEnabled, updateSingleSectionEnabled, updateSkipMarketSelection } from '../../../../../stateManagement/actions/manageCouponActions';
import SelectedMarketDetails from './SelectedMarketDetails';
import { generateRandomId, getEntityPriority, setAllPropAsEmptyArray } from '../../../services/services';
import { useSelector } from 'react-redux';
import { openPopup } from '../../../../../stateManagement/actions/popupActions';
import { COUPON_TYPE_OPTIONS_VALUE, ALL_COLUMNS_OF_MARKET_DETAILS_SLAB_ALL, DB_COLUMN_NAME_MAP_COLUMN_NAME, INVALID_INCLUSION_LIST_RESET, labelMappings } from '../../../utils/utils';
import { ConfirmationPopUp } from '../../../../../components/popup/confirmationPopUp';
import ExclusionList from './exclusionList/ExclusionList';
import InclusionList from './inclusionList/InclusionList';
import { getCombinedTabListOfMarketDetails, getMarketDetailsOptions, getMarketDetailsRowDataInFormOfState, getOutletFilterText, getUniqueValuesFromDistinctField, isExclusionListEmpty, isInclusionDisable, isInclusionListEmpty, isMarketFilterEmpty, validateCurrentMarketSelection } from './service';
import { combinedSelectedValuesType } from '../../../types';
import { useCouponAlert } from '../../../context/CouponAlert';
import { applyExclusionFilter, applyInclusionFilter, applyOverAndAboveFilter,showskipMarketDetails } from './configData';

/**
 * This is the parent component of location filter , outlet filter , inclusion list and exclusion list.
 * @returns 
 */

function MarketDetails() {
	const [values , setValues] = useState<combinedSelectedValuesType>({schemeLocationBifurcations:{} ,schemeOutletBifurcations:{}});
	const [selectorVisible , setSelectorVisible] = useState<boolean>(false);
	const {schemeLocationBifurcationsList,schemeOutletBifurcationsList , couponTypeOption , sectionEnabled , skipMarketSelection} = useSelector((state:any)=>state?.manageCoupons?.data);
	const [loading , setLoading] = useState<{market:boolean,outlet:boolean}>({
        market:false,
        outlet:false,
    });
	const [confirmationDetails , setConfirmationDetails] = useState<{message:string , open:boolean}>({message:'',open:false});
	
	const {showAlert} = useCouponAlert();

	const editRef = useRef<number | null>(null);

	const successMethodRef = useRef<()=>void>(()=>{});

    const handleChange = (name:string,newValue:Record<string,string>)=>{
        setValues((pre)=>({...pre,[name]:newValue}));
    }

	const handleAddMoreClick = ()=>{
		setSelectorVisible(true);
	}

	const resetValues = async()=>{
		editRef.current = null;
		// setting each prop value as emtpy array
		setValues({
				schemeOutletBifurcations:setAllPropAsEmptyArray(values.schemeOutletBifurcations),
				schemeLocationBifurcations: setAllPropAsEmptyArray(values.schemeLocationBifurcations)
			});
		
	}

	const handleSave = async()=>{
		// TODO : validation 
		
		const id :any = values?.schemeLocationBifurcations?.id ?? generateRandomId();
		const res:any = await validateCurrentMarketSelection(values);
		
		if(res === INVALID_INCLUSION_LIST_RESET){
			successMethodRef.current = ()=>{
				addDataToRedux(id);
			}
			setConfirmationDetails({message:labelMappings["INCLUSION_DATA_RESET"] , open:true})
			return;
		}

		else if(res === ALL_COLUMNS_OF_MARKET_DETAILS_SLAB_ALL){
			successMethodRef.current = ()=>{
				addDataToRedux(id);
			}
			setConfirmationDetails({message:labelMappings["ALL_SELECTED_IN_MARKET_SELECTION_MESSAGE"] , open:true})
			return;
		}
		addDataToRedux(id);
	}

	const addDataToRedux = (id:string)=>{
		if(Object.keys(values.schemeLocationBifurcations).length > 0 )
			addSchemeLocationBifurcationsList({id,...values.schemeLocationBifurcations});
		if(Object.keys(values.schemeOutletBifurcations).length > 0 )
			addSchemeOutletBifurcationsList({id,...values.schemeOutletBifurcations});
		
		showAlert(`Data ${editRef.current === null ? 'Saved' :'Updated'}`,'success');
		resetValues();
	}

	/**
	 * This function gets executed when edit button is clicked
	 * @param rowIndex row index which should be edited
	 * @returns 
	 */

	const handleEditButtonClick = async (rowIndex: number) => {
		// checks if editRef is true , then show popup . cannot edit 2 row at once
		// if editRef is false then edit that row

        if(editRef.current !== null){
            openPopup("Alert",labelMappings["already-editing-another-row"]);
            return;
        }
        const reformattedData = getMarketDetailsRowDataInFormOfState(rowIndex);
		handleRowDelete(rowIndex);
		setSelectorVisible(true);
        editRef.current = rowIndex;
        setValues(reformattedData);
    }


	/**
	 * This function get the options for the current tabs
	 * This function is executed when tab changes
	 * @param combinedSelectedValues 
	 * @param currentTabName 
	 * @param name 
	 * @param searchText 
	 * @returns 
	 */
	const getAllOptions = async (combinedSelectedValues:combinedSelectedValuesType, currentTabName:string,name:string,searchText?:string)=>{  
        setLoading((pre)=>({...pre , [name]:true }));
        try{
			const currTabPriority = getEntityPriority(currentTabName)
			const data: any = await getMarketDetailsOptions(combinedSelectedValues, currTabPriority, currentTabName, searchText,name);
			const newData: Record<string, string> = {};
			for (let tabName in data) {
				const colname = DB_COLUMN_NAME_MAP_COLUMN_NAME[tabName] ?? tabName
				newData[colname] = data[tabName];

			}
			return newData;
        }catch(err){
            console.error(err);
        }finally{
            setLoading((pre)=>({...pre,[name]:false}));
        }
    }

	/**
	 * Function gets executed when delete button in row is clicked
	 * @param index row index which needs to be deleted
	 * @returns 
	 */
	const handleRowDelete = (index:number)=>{
		// checks if editRef is true , then show popup . cannot perform delete while editiing
		// if editRef is false then delete that row

		if(editRef.current === null){
			deleteMarketFilter(index);
			deleteOutletFilter(index);
		}else{
			openPopup("Alert",labelMappings["cannot-delete-while-editing"]);
            return;
		}
	}

    const handleDisableMarketDetails = ()=>{

		if(skipMarketSelection === true){
			updateSkipMarketSelection(false);
		}
		else{
			
			if(!isMarketFilterEmpty() || !isInclusionListEmpty() || !isExclusionListEmpty()){
				successMethodRef.current = (()=>{
					updateSkipMarketSelection(true);
					deleteAllMarketFilters();
					deleteAllOutletFilters();
					resetInclusionData();
					resetExclusionData();
				});
				setConfirmationDetails(()=>({
					open:true,
					message:labelMappings["MARKET_DETAILS_DATA_LOST"]
				}))
			}
			else{
				updateSkipMarketSelection(true);
			}
			
		}
		
    }

	useEffect(()=>{
		if(editRef.current !==null) return;

		if(schemeLocationBifurcationsList?.length > 0){
			setSelectorVisible(false);
		}else{
			setSelectorVisible(true);
		}
	},[schemeLocationBifurcationsList])



	async function calculateEnabledSections(){
		const newVal = {...sectionEnabled};
		
		if(skipMarketSelection === true){
			newVal["marketDetails"] = false;
			newVal["inclusionList"] = false;
			newVal["exclusionList"] = false;
		}
		else{
			newVal["marketDetails"] = true;
			newVal["inclusionList"] = true;
			newVal["exclusionList"] = true;
		}

		const inclusionDisabled = await isInclusionDisable();
		if(inclusionDisabled){
			newVal["inclusionList"] = false;
		}
		updateSectionEnabled(newVal);
	}

	useEffect(()=>{
		calculateEnabledSections();
	},[couponTypeOption , skipMarketSelection,schemeOutletBifurcationsList])


	return (
		<div className='market-details-wrapper'>
			{ applyOverAndAboveFilter() && showskipMarketDetails() &&
				<CouponBox
					header={
						<div>
							<Checkbox checked={skipMarketSelection} onChange={handleDisableMarketDetails} className='select-checkbox'/>
							<CouponConfigurableLabel text={'SKIP_MARKET_SELECTION'} style={{ color: 'inherit', cursor: 'default' }} />
						</div>
					}
				><></>
				</CouponBox>
			}
			<div>
				<CouponBox disabled={!sectionEnabled["marketDetails"]} header={<CouponConfigurableLabel text={'define-retailer-universe'} style={{ color: 'inherit' }} />}>
					<div className="market-details-wrapper-header">
						{
							selectorVisible ? <Button variant='contained' className='market-details-save-btn' size='small' startIcon={<DoneIcon />} onClick={handleSave}>{editRef.current!==null?'Update':'Save'}</Button>
							:
							<Button className='market-details-addmore-btn' onClick={handleAddMoreClick} size='small' startIcon={<AddCircleOutlineOutlined />}>Add More</Button>
						}
					</div>
					<div className="market-detials-wrapper-content">
						{
							selectorVisible && <MarketDetailsSelector 
								handleChange={handleChange} 
								values={values}
								editMode = {editRef.current!==null}
								loading={loading}
								getAllOptions={getAllOptions}
								/>
						}
						<SelectedMarketDetails
								handleRowEdit={handleEditButtonClick} 
								handleRowDelete={handleRowDelete}
								editingRow={false} 
								tabsList={getCombinedTabListOfMarketDetails()}
							/>
					</div>
				</CouponBox>
						
				<div className='inc-exc-wrapper'>
					{applyInclusionFilter() && <InclusionList disable={!sectionEnabled["inclusionList"] || !sectionEnabled["marketDetails"]} disabledMessage={!sectionEnabled["marketDetails"] ? labelMappings["MARKET_DETAILS_DISABLED"]:undefined} /> }
					{applyExclusionFilter() && <ExclusionList disable={!sectionEnabled["exclusionList"] || !sectionEnabled["marketDetails"]} disabledMessage={!sectionEnabled["marketDetails"] ? labelMappings["MARKET_DETAILS_DISABLED"]:undefined}/>}
					
				</div>

				<ConfirmationPopUp
					message={confirmationDetails.message}
					openConfirmModal={confirmationDetails.open}
					setOpenConfirmModal={(val)=>setConfirmationDetails(()=>({message:"",open:val as boolean}))}
					successMethod={successMethodRef.current}
				/>
			</div>
		</div>
	)
}

export default React.memo(MarketDetails);